import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  TextField, 
  Button, 
  CircularProgress,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Card,
  CardContent,
  Avatar,
  Fade,
  ListItemIcon
} from '@mui/material';
import dayjs from 'dayjs';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  TableChart as TableIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useForm, Controller, SubmitHandler, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PageHeader from '../components/common/PageHeader';
import AlertMessage from '../components/common/AlertMessage';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { UserService } from '../services/user.service';
import { TableService } from '../services/table.service';
import { TableDataService } from '../services/tableData.service';
import { TransferService } from '../services/transfer.service';
import { User } from '../types/auth.types';
import { DataTable } from '../types/table.types';
import { TableData } from '../types/tableData.types';
import { CreateTransferRequest, CreateTransferItemRequest } from '../types/transfer.types';

interface FormData {
  targetUserId: string;
  description?: string;
}

const schema = yup.object().shape({
  targetUserId: yup.string().required('Получатель обязателен'),
  description: yup.string().notRequired(),
});

export default function TransferCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTableId = queryParams.get('tableId');
  const initialDataId = queryParams.get('dataId');
  
  const [users, setUsers] = useState<User[]>([]);
  const [tables, setTables] = useState<DataTable[]>([]);
  const [selectedTable, setSelectedTable] = useState<DataTable | null>(null);
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [items, setItems] = useState<CreateTransferItemRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Record<string, boolean>>({});
  const [mounted, setMounted] = useState(false);
  
  const { control, handleSubmit, formState: { errors }, setValue } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
    defaultValues: {
      targetUserId: '',
      description: '',
    }
  });
  
  useEffect(() => {
    setMounted(true);
    fetchInitialData();
  }, []);
  
  useEffect(() => {
    if (initialTableId && initialDataId) {
      addPreselectedItem();
    }
  }, [initialTableId, initialDataId, tables]);
  
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      const usersData = await UserService.getAllUsers();
      setUsers(usersData);
      
      const tablesData = await TableService.getAllTables();
      setTables(tablesData);
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };
  
  const addPreselectedItem = async () => {
    if (!initialTableId || !initialDataId || !tables.length) return;
    
    try {
      const table = tables.find(t => t.id === initialTableId);
      if (!table) return;
      
      const data = await TableDataService.getTableDataById(initialTableId, initialDataId);
      
      const newItem: CreateTransferItemRequest = {
        sourceTableId: initialTableId,
        sourceDataId: initialDataId,
      };
      
      setItems(prev => {
        const exists = prev.some(item => 
          item.sourceTableId === newItem.sourceTableId && 
          item.sourceDataId === newItem.sourceDataId
        );
        
        if (exists) return prev;
        return [...prev, newItem];
      });
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки предварительно выбранных данных');
    }
  };
  
  const handleTableChange = async (tableId: string) => {
    try {
      const table = tables.find(t => t.id === tableId);
      setSelectedTable(table || null);
      
      const response = await TableDataService.getTableData(tableId, { pageSize: 100 });
      setTableData(response.data);
      
      setSelectedItems({});
      
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка загрузки данных таблицы');
    }
  };
  
  const handleAddItems = () => {
    const newItems = Object.entries(selectedItems)
      .filter(([_, isSelected]) => isSelected)
      .map(([dataId]) => ({
        sourceTableId: selectedTable!.id,
        sourceDataId: dataId,
      }));
    
    setItems(prev => {
      const updated = [...prev];
      
      newItems.forEach(newItem => {
        const exists = updated.some(item => 
          item.sourceTableId === newItem.sourceTableId && 
          item.sourceDataId === newItem.sourceDataId
        );
        
        if (!exists) {
          updated.push(newItem);
        }
      });
      
      return updated;
    });
    
    setSelectedItems({});
    setShowItemDialog(false);
  };
  
  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };
  
  const handleCancel = () => {
    navigate('/transfers');
  };
  
  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (items.length === 0) {
      setError('Необходимо добавить хотя бы один элемент для передачи');
      return;
    }
  
    try {
      setSubmitting(true);
      
      const transferData: CreateTransferRequest = {
        targetUserId: data.targetUserId,
        description: data.description,
        items,
      };
  
      await TransferService.createTransfer(transferData);
      setSuccess('Передача создана успешно');
      
      setTimeout(() => {
        navigate('/transfers');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка создания передачи');
    } finally {
      setSubmitting(false);
    }
  };
  
  const getTableName = (tableId: string) => {
    const table = tables.find(t => t.id === tableId);
    return table ? table.name : 'Неизвестная таблица';
  };
  
  const getDataDescription = (tableId: string, dataId: string) => {
    return `Запись ${dataId.substring(0, 8)}...`;
  };
  
  return (
    <Box>
      <Fade in={mounted} timeout={400}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 3,
            p: 4,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-50%',
              right: '-20%',
              width: '400px',
              height: '400px',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                <SendIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Создание передачи
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Выберите получателя и данные для передачи
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <Fade in={mounted} timeout={600}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', p: 2.5, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Получатель
                </Typography>
              </Box>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="targetUserId"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Выберите получателя"
                        fullWidth
                        required
                        error={!!errors.targetUserId}
                        helperText={errors.targetUserId?.message}
                        disabled={loading || submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      >
                        {users.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 32, height: 32, bgcolor: '#4facfe', fontSize: '0.875rem' }}>
                                {user.fullName.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography variant="body1">{user.fullName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  @{user.username}
                                </Typography>
                              </Box>
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Описание (необязательно)"
                        fullWidth
                        multiline
                        rows={3}
                        error={!!errors.description}
                        helperText={errors.description?.message}
                        disabled={loading || submitting}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': { borderColor: '#4facfe' },
                            '&.Mui-focused fieldset': { borderColor: '#4facfe' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>
        
        <Fade in={mounted} timeout={800}>
          <Card sx={{ mb: 3, borderRadius: 3 }}>
            <Box sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', p: 2.5, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <TableIcon />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Элементы для передачи
                  </Typography>
                  <Chip 
                    label={items.length}
                    size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                  />
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowItemDialog(true)}
                  disabled={loading || submitting}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                  }}
                >
                  Добавить элементы
                </Button>
              </Box>
            </Box>
            
            <CardContent sx={{ p: 3 }}>
              {items.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <TableIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    Нет выбранных элементов для передачи
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Нажмите "Добавить элементы", чтобы выбрать данные
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {items.map((item, index) => (
                    <ListItem 
                      key={index} 
                      divider={index < items.length - 1}
                      sx={{
                        py: 2,
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'rgba(240, 147, 251, 0.05)' }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ bgcolor: 'rgba(245, 87, 108, 0.1)', color: '#f5576c' }}>
                          <TableIcon />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {getTableName(item.sourceTableId)}
                          </Typography>
                        }
                        secondary={getDataDescription(item.sourceTableId, item.sourceDataId)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          onClick={() => handleRemoveItem(index)}
                          disabled={submitting}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Fade>
        
        <Fade in={mounted} timeout={1000}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleCancel}
              disabled={submitting}
              startIcon={<CancelIcon />}
              sx={{
                borderColor: '#667eea',
                color: '#667eea',
                '&:hover': {
                  borderColor: '#667eea',
                  bgcolor: 'rgba(102, 126, 234, 0.08)'
                }
              }}
            >
              Отмена
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={loading || submitting || items.length === 0}
              startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)'
                }
              }}
            >
              {submitting ? 'Отправка...' : 'Отправить'}
            </Button>
          </Box>
        </Fade>
      </form>
      
      <Dialog 
        open={showItemDialog} 
        onClose={() => setShowItemDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          pb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TableIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Выбор элементов для передачи
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              select
              label="Таблица"
              fullWidth
              value={selectedTable?.id || ''}
              onChange={(e) => handleTableChange(e.target.value)}
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: '#f5576c' },
                  '&.Mui-focused fieldset': { borderColor: '#f5576c' }
                }
              }}
            >
              {tables.map((table) => (
                <MenuItem key={table.id} value={table.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TableIcon fontSize="small" sx={{ color: '#f5576c' }} />
                    {table.name}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            
            {selectedTable && (
              <>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Выберите записи для передачи:
                </Typography>
                
                <Box sx={{ 
                  maxHeight: 400, 
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1
                }}>
                  <List>
                    {tableData.map((data) => (
                      <ListItem 
                        key={data.id}
                        sx={{
                          borderRadius: 1,
                          '&:hover': { bgcolor: 'rgba(245, 87, 108, 0.05)' }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={!!selectedItems[data.id]}
                            onChange={(e) => {
                              setSelectedItems({
                                ...selectedItems,
                                [data.id]: e.target.checked
                              });
                            }}
                            sx={{
                              color: '#f5576c',
                              '&.Mui-checked': { color: '#f5576c' }
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Запись ${data.id.substring(0, 8)}...`}
                          secondary={`Создана: ${dayjs(data.createdAt).format('DD.MM.YYYY HH:mm')}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setShowItemDialog(false)}
            startIcon={<CancelIcon />}
          >
            Отмена
          </Button>
          <Button 
            onClick={handleAddItems} 
            variant="contained"
            startIcon={<CheckIcon />}
            disabled={Object.values(selectedItems).filter(Boolean).length === 0}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e082ea 0%, #e4465b 100%)'
              }
            }}
          >
            Добавить выбранные ({Object.values(selectedItems).filter(Boolean).length})
          </Button>
        </DialogActions>
      </Dialog>
      
      <AlertMessage 
        open={!!error} 
        message={error || ''} 
        severity="error" 
        onClose={() => setError(null)} 
      />
      
      <AlertMessage 
        open={!!success} 
        message={success || ''} 
        severity="success" 
        onClose={() => setSuccess(null)} 
      />
    </Box>
  );
}